import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  FormHelperText,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Snackbar,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add as AddIcon,
  Delete as DeleteIcon,
  Rule as RuleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Block as BlockIcon,
  CheckCircle,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { suppressionListsAPI, suppressionRulesAPI } from '../../../services/leadGenAPI';
import { api } from '../../../services/authService';
import { useSelector } from 'react-redux';

const EditSuppressionList = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const currentUser = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'contact',
    list_type: 'blacklist', // 'blacklist', 'whitelist'
    scope: 'global',
    status: 'active',
    account_id: '', // Required field
    rules: [],
  });

  // Account selection state
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [newRule, setNewRule] = useState({
    field: '',
    operator: '',
    value: '',
  });

  const fieldOptions = {
    contact: [
      { value: 'email', label: 'Email Address' },
      { value: 'phone', label: 'Phone Number' },
      { value: 'first_name', label: 'First Name' },
      { value: 'last_name', label: 'Last Name' },
      { value: 'company_domain', label: 'Company Domain' },
    ],
    company: [
      { value: 'company_name', label: 'Company Name' },
      { value: 'company_domain', label: 'Company Domain' },
      { value: 'industry', label: 'Industry' },
      { value: 'company_size', label: 'Company Size' },
    ],
    geographic: [
      { value: 'country', label: 'Country' },
      { value: 'state', label: 'State/Province' },
      { value: 'city', label: 'City' },
      { value: 'zipcode', label: 'ZIP/Postal Code' },
    ],
    demographic: [
      { value: 'age', label: 'Age' },
      { value: 'gender', label: 'Gender' },
      { value: 'income_level', label: 'Income Level' },
      { value: 'education', label: 'Education Level' },
    ],
  };

  const operatorOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Not Contains' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' },
    { value: 'in', label: 'In' },
    { value: 'not_in', label: 'Not In' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' },
  ];

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Enhanced rules table features
  const [rulesPage, setRulesPage] = useState(0);
  const [rulesRowsPerPage, setRulesRowsPerPage] = useState(10);
  const [rulesSearchTerm, setRulesSearchTerm] = useState('');
  const [filteredRules, setFilteredRules] = useState([]);

  // Rule management state
  const [deletedRuleIds, setDeletedRuleIds] = useState([]);
  const [ruleOperations, setRuleOperations] = useState([]); // Track pending operations
  const [deleteRuleDialog, setDeleteRuleDialog] = useState({ open: false, ruleId: null, ruleName: '' });

  // Fetch accounts on component mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (id) {
      fetchListDetails();
    }
  }, [id]);

  // Apply rules filtering and search
  useEffect(() => {
    applyRulesFilters();
  }, [formData.rules, rulesSearchTerm]);

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await api.get('/crm/accounts/');
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchListDetails = async () => {
    try {
      const response = await suppressionListsAPI.getList(id);
      setFormData({
        name: response.name || '',
        description: response.description || '',
        type: response.suppression_type || 'contact',
        list_type: response.list_type || 'blacklist',
        scope: response.is_global ? 'global' : 'campaign_specific',
        status: response.status || 'active',
        account_id: response.account_id || '',
        rules: response.suppression_rules || [],
      });
    } catch (error) {
      console.error('Error fetching list details:', error);
      showSnackbar(`Failed to fetch list details: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'List name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.account_id) {
      newErrors.account_id = 'Account is required';
    }

    if (formData.rules.length === 0) {
      newErrors.rules = 'At least one rule is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      // First, handle rule deletions
      for (const ruleId of deletedRuleIds) {
        try {
          await suppressionRulesAPI.deleteRule(id, ruleId);
        } catch (error) {
          console.error(`Failed to delete rule ${ruleId}:`, error);
          // Continue with other operations even if one deletion fails
        }
      }

      // Then, handle rule additions
      const newRules = formData.rules.filter(rule => rule.is_new);
      for (const rule of newRules) {
        try {
          const ruleData = {
            rule_type: rule.field,
            field_name: rule.field,
            operator: rule.operator,
            value: rule.value,
            is_active: true,
            priority: 0
          };
          await suppressionRulesAPI.createRule(id, ruleData);
        } catch (error) {
          console.error(`Failed to create rule:`, error);
          // Continue with other operations even if one creation fails
        }
      }

      // Finally, update the suppression list basic info
      const suppressionListData = {
        name: formData.name,
        description: formData.description,
        suppression_type: formData.type,
        list_type: formData.list_type,
        is_global: formData.scope === 'global',
        status: formData.status,
        account_id: formData.account_id,
        created_by_id: currentUser?.id,
      };

      await suppressionListsAPI.updateList(id, suppressionListData);
      
      // Clear operation tracking
      setDeletedRuleIds([]);
      setRuleOperations([]);
      
      showSnackbar('Suppression list updated successfully', 'success');
      navigate(`/lead-gen/suppression/${id}`);
    } catch (error) {
      console.error('Error updating suppression list:', error);
      showSnackbar(`Failed to update suppression list: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addRule = () => {
    // Check if required fields are filled
    const isValueRequired = newRule.operator !== 'is_empty' && newRule.operator !== 'is_not_empty';
    const isValid = newRule.field && newRule.operator && (!isValueRequired || newRule.value);
    
    if (isValid) {
      const newRuleWithId = { 
        ...newRule, 
        id: `temp_${Date.now()}`, // Temporary ID for new rules
        is_new: true // Flag to identify new rules
      };
      
      setFormData(prev => ({
        ...prev,
        rules: [...prev.rules, newRuleWithId]
      }));
      
      // Track the operation
      setRuleOperations(prev => [...prev, { type: 'add', rule: newRuleWithId }]);
      
      setNewRule({ field: '', operator: '', value: '' });
      if (errors.rules) {
        setErrors(prev => ({ ...prev, rules: '' }));
      }
    }
  };

  const removeRule = (ruleId) => {
    const ruleToDelete = formData.rules.find(rule => rule.id === ruleId);
    
    // Remove from form data
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.filter(rule => rule.id !== ruleId)
    }));
    
    // Track deletion
    if (ruleToDelete && !ruleToDelete.is_new) {
      // Only track deletions for existing rules (not newly added ones)
      setDeletedRuleIds(prev => [...prev, ruleId]);
      setRuleOperations(prev => [...prev, { type: 'delete', ruleId }]);
    } else if (ruleToDelete && ruleToDelete.is_new) {
      // Remove the add operation for new rules
      setRuleOperations(prev => prev.filter(op => !(op.type === 'add' && op.rule.id === ruleId)));
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'contact': return <PersonIcon />;
      case 'company': return <BusinessIcon />;
      case 'geographic': return <LocationIcon />;
      case 'demographic': return <PersonIcon />;
      default: return <BlockIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'contact': return 'primary';
      case 'company': return 'secondary';
      case 'geographic': return 'info';
      case 'demographic': return 'warning';
      default: return 'default';
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const applyRulesFilters = () => {
    let filtered = [...formData.rules];

    // Apply search
    if (rulesSearchTerm) {
      filtered = filtered.filter(rule =>
        (rule.field_name || rule.field)?.toLowerCase().includes(rulesSearchTerm.toLowerCase()) ||
        rule.operator?.toLowerCase().includes(rulesSearchTerm.toLowerCase()) ||
        rule.value?.toLowerCase().includes(rulesSearchTerm.toLowerCase()) ||
        rule.rule_type?.toLowerCase().includes(rulesSearchTerm.toLowerCase())
      );
    }

    setFilteredRules(filtered);
    setRulesPage(0); // Reset to first page when filters change
  };

  const clearRulesSearch = () => {
    setRulesSearchTerm('');
  };

  const handleImmediateRuleDelete = async (ruleId) => {
    const ruleToDelete = formData.rules.find(rule => rule.id === ruleId);
    
    if (!ruleToDelete) return;

    try {
      // If it's an existing rule (not newly added), delete it immediately
      if (!ruleToDelete.is_new) {
        await suppressionRulesAPI.deleteRule(id, ruleId);
        showSnackbar('Rule deleted successfully', 'success');
      }
      
      // Remove from form data
      setFormData(prev => ({
        ...prev,
        rules: prev.rules.filter(rule => rule.id !== ruleId)
      }));
      
      // Clear from tracking if it was a new rule
      if (ruleToDelete.is_new) {
        setRuleOperations(prev => prev.filter(op => !(op.type === 'add' && op.rule.id === ruleId)));
      }
      
    } catch (error) {
      console.error('Error deleting rule:', error);
      showSnackbar(`Failed to delete rule: ${error.message}`, 'error');
    }
  };

  const confirmDeleteRule = (ruleId) => {
    const rule = formData.rules.find(r => r.id === ruleId);
    if (rule) {
      const ruleName = `${rule.field_name || rule.field} ${rule.operator} "${rule.value}"`;
      setDeleteRuleDialog({ open: true, ruleId, ruleName });
    }
  };

  const handleConfirmDeleteRule = async () => {
    if (deleteRuleDialog.ruleId) {
      await handleImmediateRuleDelete(deleteRuleDialog.ruleId);
      setDeleteRuleDialog({ open: false, ruleId: null, ruleName: '' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/lead-gen/suppression/${id}`)}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" color="text.primary">
            Edit Suppression List
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                Basic Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="List Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    error={!!errors.description}
                    helperText={errors.description}
                    multiline
                    rows={3}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type"
                      onChange={(e) => handleInputChange('type', e.target.value)}
                    >
                      <MenuItem value="contact">Contact</MenuItem>
                      <MenuItem value="company">Company</MenuItem>
                      <MenuItem value="geographic">Geographic</MenuItem>
                      <MenuItem value="demographic">Demographic</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>List Type</InputLabel>
                    <Select
                      value={formData.list_type}
                      label="List Type"
                      onChange={(e) => handleInputChange('list_type', e.target.value)}
                    >
                      <MenuItem value="blacklist">
                        <Box display="flex" alignItems="center" gap={1}>
                          <BlockIcon color="error" />
                          Blacklist (Exclude matching leads)
                        </Box>
                      </MenuItem>
                      <MenuItem value="whitelist">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircle color="success" />
                          Whitelist (Only allow matching leads)
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.account_id}>
                    <InputLabel>Account *</InputLabel>
                    <Select
                      value={formData.account_id}
                      label="Account *"
                      onChange={(e) => handleInputChange('account_id', e.target.value)}
                      disabled={accountsLoading}
                    >
                      {accounts.map(account => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.account_id && (
                      <FormHelperText>{errors.account_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={formData.scope}
                      label="Scope"
                      onChange={(e) => handleInputChange('scope', e.target.value)}
                    >
                      <MenuItem value="global">Global</MenuItem>
                      <MenuItem value="campaign_specific">Campaign Specific</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status === 'active'}
                        onChange={(e) => handleInputChange('status', e.target.checked ? 'active' : 'inactive')}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Rules Configuration */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                Suppression Rules
              </Typography>
              
              {errors.rules && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.rules}
                </Alert>
              )}

              {/* Add New Rule */}
              <Box sx={{ mb: 3, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add New Rule
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Field</InputLabel>
                      <Select
                        value={newRule.field}
                        label="Field"
                        onChange={(e) => setNewRule(prev => ({ ...prev, field: e.target.value }))}
                      >
                        {fieldOptions[formData.type]?.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={newRule.operator}
                        label="Operator"
                        onChange={(e) => setNewRule(prev => ({ ...prev, operator: e.target.value }))}
                      >
                        {operatorOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Value"
                      value={newRule.value}
                      onChange={(e) => setNewRule(prev => ({ ...prev, value: e.target.value }))}
                      disabled={newRule.operator === 'is_empty' || newRule.operator === 'is_not_empty'}
                      placeholder={newRule.operator === 'is_empty' || newRule.operator === 'is_not_empty' ? 'Not required for this operator' : 'Enter value...'}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={addRule}
                      disabled={(() => {
                        const isValueRequired = newRule.operator !== 'is_empty' && newRule.operator !== 'is_not_empty';
                        return !newRule.field || !newRule.operator || (isValueRequired && !newRule.value);
                      })()}
                      fullWidth
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Pending Operations Summary */}
              {(deletedRuleIds.length > 0 || formData.rules.filter(r => r.is_new).length > 0) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Pending Changes:</strong>
                    {deletedRuleIds.length > 0 && ` ${deletedRuleIds.length} rule(s) to delete`}
                    {formData.rules.filter(r => r.is_new).length > 0 && ` ${formData.rules.filter(r => r.is_new).length} new rule(s) to add`}
                  </Typography>
                </Alert>
              )}

              {/* Existing Rules */}
              {formData.rules.length > 0 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">
                      Current Rules ({filteredRules.length} of {formData.rules.length})
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Search rules..."
                      value={rulesSearchTerm}
                      onChange={(e) => setRulesSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: rulesSearchTerm && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={clearRulesSearch}
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 200 }}
                    />
                  </Box>
                  
                  {filteredRules.length > 0 ? (
                    <Box>
                      <List>
                        {filteredRules
                          .slice(rulesPage * rulesRowsPerPage, rulesPage * rulesRowsPerPage + rulesRowsPerPage)
                          .map((rule, index) => (
                            <ListItem
                              key={rule.id}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1,
                                backgroundColor: 'background.paper',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                  borderColor: 'primary.main',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  onClick={() => confirmDeleteRule(rule.id)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              }
                            >
                              <ListItemIcon>
                                <RuleIcon color="primary" />
                              </ListItemIcon>
                                                      <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body1" color="text.primary" fontWeight={500}>
                                {(() => {
                                  const isValueOperator = rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty';
                                  return isValueOperator 
                                    ? `${rule.field_name || rule.field} ${rule.operator} "${rule.value}"`
                                    : `${rule.field_name || rule.field} ${rule.operator}`;
                                })()}
                              </Typography>
                              {rule.is_new && (
                                <Chip
                                  label="New"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              Rule {rulesPage * rulesRowsPerPage + index + 1} - {rule.rule_type || rule.field}
                            </Typography>
                          }
                        />
                            </ListItem>
                          ))}
                      </List>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredRules.length}
                        rowsPerPage={rulesRowsPerPage}
                        page={rulesPage}
                        onPageChange={(event, newPage) => setRulesPage(newPage)}
                        onRowsPerPageChange={(event) => {
                          setRulesRowsPerPage(parseInt(event.target.value, 10));
                          setRulesPage(0);
                        }}
                        labelDisplayedRows={({ from, to, count }) => 
                          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                        }
                        labelRowsPerPage="Rules per page:"
                      />
                    </Box>
                  ) : (
                    <Box py={3}>
                      <Typography variant="body2" color="textSecondary" gutterBottom align="center">
                        {rulesSearchTerm
                          ? 'No rules match your search criteria'
                          : 'No rules configured for this suppression list.'
                        }
                      </Typography>
                      {rulesSearchTerm && (
                        <Box display="flex" justifyContent="center" mt={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={clearRulesSearch}
                            startIcon={<ClearIcon />}
                          >
                            Clear Search
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                Preview
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  List Details
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {getTypeIcon(formData.type)}
                  <Chip 
                    label={formData.type} 
                    size="small" 
                    color={getTypeColor(formData.type)}
                    variant="outlined"
                  />
                  <Chip 
                    label={formData.list_type === 'whitelist' ? 'Whitelist' : 'Blacklist'} 
                    size="small" 
                    color={formData.list_type === 'whitelist' ? 'success' : 'error'}
                    variant="outlined"
                    icon={formData.list_type === 'whitelist' ? <CheckCircle /> : <BlockIcon />}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  <strong>Name:</strong> {formData.name || 'Not specified'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>List Type:</strong> {formData.list_type === 'whitelist' ? 'Whitelist (Only allow matching leads)' : 'Blacklist (Exclude matching leads)'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Account:</strong> {accounts.find(acc => acc.id === formData.account_id)?.name || 'Not selected'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Scope:</strong> {formData.scope === 'global' ? 'Global' : 'Campaign Specific'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Status:</strong> {formData.status}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Rules:</strong> {formData.rules.length}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formData.description || 'No description provided'}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Rules Summary
              </Typography>
              {formData.rules.length > 0 ? (
                <Box>
                  {formData.rules.slice(0, 3).map((rule, index) => (
                    <Typography key={rule.id} variant="caption" display="block" color="textSecondary">
                      {(() => {
                        const isValueOperator = rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty';
                        return isValueOperator 
                          ? `• ${rule.field_name || rule.field} ${rule.operator} "${rule.value}"`
                          : `• ${rule.field_name || rule.field} ${rule.operator}`;
                      })()}
                    </Typography>
                  ))}
                  {formData.rules.length > 3 && (
                    <Typography variant="caption" color="textSecondary">
                      ... and {formData.rules.length - 3} more rules
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  No rules configured
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Rule Confirmation Dialog */}
      <Dialog
        open={deleteRuleDialog.open}
        onClose={() => setDeleteRuleDialog({ open: false, ruleId: null, ruleName: '' })}
      >
        <DialogTitle>Confirm Rule Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the rule: <strong>{deleteRuleDialog.ruleName}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteRuleDialog({ open: false, ruleId: null, ruleName: '' })}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteRule}
            color="error"
            variant="contained"
          >
            Delete Rule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditSuppressionList; 